import { AdsOrderStatus } from "@/interface/order";
import { AdsNextOperateType } from "@/components/config";
import { isFeishuEnv } from "@/utils/feishu";
import { isMobile } from "./tool";

// 订单价格协商状态
export enum OrderPriceNegotiationStatus {
  // 第一次报价待广告主审核
  UnderAdsFirstReview = "Under_ads_first_review",
  // 报价待广告主审核
  UnderAdsReview = "Under_ads_review",
  // 报价待达人审核
  UnderCreatorReview = "Under_creator_review",
  // 达人同意价格待广告主付款
  PendingAdsApproval = "Pending_ads_approval",
}

export const showOrderStep = (status: AdsOrderStatus) => {
  switch (status) {
    case AdsOrderStatus.CollaborationEstablished:
      return false;
    case AdsOrderStatus.PendingProductShipment:
    case AdsOrderStatus.PendingProductArrival:
    case AdsOrderStatus.UnderScriptProduction:
    case AdsOrderStatus.UnderContentProduction:
    case AdsOrderStatus.UnderScriptReview:
    case AdsOrderStatus.UnderContentReview:
    case AdsOrderStatus.AdsPendingPublish:
    case AdsOrderStatus.AdsPublished:
      return true;
    case AdsOrderStatus.CollaborationCancelled:
      return false;
    default:
      return false;
  }
};

export const showOrderMainButton = (
  status: AdsOrderStatus,
  orderDetail: any,
) => {
  switch (status) {
    case AdsOrderStatus.QuoteRequested:
      if (orderDetail?.ahaPicksOffer) {
        return ["RemoveInfluencer", "ahaPicksSendOffer"];
      }
      return ['RemoveInfluencer'];
    case AdsOrderStatus.PendingSelection:
      return ["ahaPicksRemove", "ahaPicksSendOffer", "ahaPicksConfirm"];
    case AdsOrderStatus.CollaborationEstablished:
      let buttons = ["Remove", "Confirm"];
      if (isMobile() || isFeishuEnv()) {
        buttons = ["Confirm", "Remove"];
      }
      if (orderDetail?.isCancelable) {
        buttons.unshift("Terminate collaboration");
      }
      // 更新：提交价格后可继续议价
      if (
        orderDetail?.priceNegotiationStatus !==
        OrderPriceNegotiationStatus.PendingAdsApproval
      ) {
        buttons.unshift("Negotiate price");
      }
      return buttons;
    case AdsOrderStatus.PendingProductShipment:
      const shipmentButtons = [
        // "Postpone the timeline",
        "Enter shipping information",
      ];
      if (orderDetail?.isCancelable) {
        shipmentButtons.unshift("Terminate collaboration");
      }
      return shipmentButtons;
    case AdsOrderStatus.PendingProductArrival:
    case AdsOrderStatus.UnderScriptProduction:
    case AdsOrderStatus.UnderContentProduction:
    case AdsOrderStatus.UnderScriptReview:
    case AdsOrderStatus.UnderContentReview:
    case AdsOrderStatus.AdsPendingPublish:
      if (orderDetail?.isCancelable) {
        return ["Terminate collaboration"];
      }
      return [];
    case AdsOrderStatus.AdsPublished:
      if (orderDetail?.originalStatus === "Pending_payment") {
        return [];
      } else {
        return [];
      }
    case AdsOrderStatus.CollaborationCancelled:
      return [];
    case AdsOrderStatus.PendingAccountSetup:
      return ["Request change", "Confirm access setup"];
    default:
      if (orderDetail?.isCancelable) {
        return ["Terminate collaboration"];
      }
      return [];
  }
};

export const showContentButton = (
  status: AdsOrderStatus,
  adsNextOperateType: AdsNextOperateType,
) => {
  // Postpone the timeline
  // Provide feedback
  // Approve
  switch (status) {
    case AdsOrderStatus.CollaborationEstablished:
    case AdsOrderStatus.UnderScriptProduction:
    case AdsOrderStatus.UnderContentProduction:
      return [];
    case AdsOrderStatus.UnderScriptReview:
    case AdsOrderStatus.UnderContentReview:
      if (adsNextOperateType === AdsNextOperateType.Draft_5_Approve) {
        return ["Approve"];
        // return ["Postpone the timeline", "Approve"];
      }
      return ["Provide feedback", "Approve"];
    // return ["Postpone the timeline", "Provide feedback", "Approve"];
    case AdsOrderStatus.AdsPendingPublish:
      return [];
    case AdsOrderStatus.AdsPublished:
      return [];
    case AdsOrderStatus.CollaborationCancelled:
      return [];
    default:
      return [];
  }
};

export const getPrevId = (currentOrderId: string, orderList: string[]) => {
  const index = orderList?.findIndex((id: string) => id === currentOrderId);
  // return index > 0 ? orderList[index - 1] : null;
  return orderList?.[index - 1];
};

export const getNextId = (currentOrderId: string, orderList: string[]) => {
  const index = orderList?.findIndex((id: string) => id === currentOrderId);
  return index < orderList?.length - 1 ? orderList?.[index + 1] : null;
};

export const getCurrentIndex = (
  currentOrderId: string,
  orderList: string[],
) => {
  return orderList?.findIndex((id: string) => id === currentOrderId);
};

/** 与 getCampaignTodos / pending 列表配合：订单详情页待办顺序 */
/** 规则 2：与当前订单同 status 时，CollaborationEstablished 下按协商子状态细分次序（与规则 3–6 对齐） */
function sameStatusAsOrderSubKey(item: {
  status: AdsOrderStatus;
  priceNegotiationStatus?: string;
}): number {
  if (item.status !== AdsOrderStatus.CollaborationEstablished) {
    return 0;
  }
  const pn = item.priceNegotiationStatus;
  if (pn === OrderPriceNegotiationStatus.PendingAdsApproval) return 0;
  if (pn === OrderPriceNegotiationStatus.UnderAdsReview) return 1;
  if (pn === OrderPriceNegotiationStatus.UnderAdsFirstReview) return 2;
  if (pn === OrderPriceNegotiationStatus.UnderCreatorReview) return 3;
  return 4;
}

/**
 * 规则 3–10（已与当前订单 status 不同的项）
 * 3 CE+PendingAdsApproval → 0
 * 4 CE+UnderAdsReview → 1
 * 5 PendingSelection → 2
 * 6 CE+UnderAdsFirstReview → 3
 * 7–10 PendingAccountSetup / PendingProductShipment / UnderScriptReview / UnderContentReview
 */
function restPendingOrderPriority(item: {
  status: AdsOrderStatus;
  priceNegotiationStatus?: string;
}): number {
  const { status, priceNegotiationStatus: pn } = item;
  if (status === AdsOrderStatus.CollaborationEstablished) {
    if (pn === OrderPriceNegotiationStatus.PendingAdsApproval) return 0;
    if (pn === OrderPriceNegotiationStatus.UnderAdsReview) return 1;
    if (pn === OrderPriceNegotiationStatus.UnderAdsFirstReview) return 3;
  }
  if (status === AdsOrderStatus.PendingSelection) return 2;
  if (status === AdsOrderStatus.PendingAccountSetup) return 4;
  if (status === AdsOrderStatus.PendingProductShipment) return 5;
  if (status === AdsOrderStatus.UnderScriptReview) return 6;
  if (status === AdsOrderStatus.UnderContentReview) return 7;
  return 100;
}

/** 返回字典序比较的 key，越小越靠前 */
export function getCampaignPendingOrderSortKey(
  item: { id: string; status: AdsOrderStatus; priceNegotiationStatus?: string },
  orderDetail: {
    id?: string;
    status?: AdsOrderStatus;
    priceNegotiationStatus?: string;
  },
): [number, number, number] {
  const currentId = orderDetail?.id;
  const currentStatus = orderDetail?.status;
  const orderPn = orderDetail?.priceNegotiationStatus;

  // 1. 当前订单
  if (currentId != null && item.id === currentId) {
    return [0, 0, 0];
  }

  // 2. 与 orderDetail 同 status（非当前 id）
  if (currentStatus != null && item.status === currentStatus) {
    // CollaborationEstablished：与当前订单 priceNegotiationStatus 相同的排在前面，再按协商子状态次序
    const usePnTier =
      currentStatus === AdsOrderStatus.CollaborationEstablished &&
      orderPn != null &&
      orderPn !== "";
    if (usePnTier) {
      const tier = item.priceNegotiationStatus === orderPn ? 0 : 1;
      return [1, tier, sameStatusAsOrderSubKey(item)];
    }
    return [1, sameStatusAsOrderSubKey(item), 0];
  }

  // 3–10
  return [2, restPendingOrderPriority(item), 0];
}

export function sortCampaignPendingOrders<
  T extends {
    id: string;
    status: AdsOrderStatus;
    priceNegotiationStatus?: string;
  },
>(
  items: T[],
  orderDetail: {
    id?: string;
    status?: AdsOrderStatus;
    priceNegotiationStatus?: string;
  },
): T[] {
  return [...(items || [])].sort((a, b) => {
    const ka = getCampaignPendingOrderSortKey(a, orderDetail);
    const kb = getCampaignPendingOrderSortKey(b, orderDetail);
    for (let i = 0; i < ka.length; i++) {
      if (ka[i] !== kb[i]) return ka[i] - kb[i];
    }
    // 元组相同时：CE 下按协商状态字符串聚类，避免未命中枚举时落到同一 subKey 导致不同状态交错
    if (
      a.status === AdsOrderStatus.CollaborationEstablished &&
      b.status === AdsOrderStatus.CollaborationEstablished
    ) {
      const pnCmp = String(a.priceNegotiationStatus ?? "").localeCompare(
        String(b.priceNegotiationStatus ?? ""),
      );
      if (pnCmp !== 0) return pnCmp;
    }
    return String(a.id).localeCompare(String(b.id));
  });
}
